import { TutorialScrollLockDirective } from '../../../../shared/directives/tutorial-scroll-lock.directive';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { RouterModule } from '@angular/router';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatListModule } from '@angular/material/list';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { Subscription, tap } from 'rxjs';
import { AuthService } from '../../../../core/services/auth.service';
import { TranslateModule } from '@ngx-translate/core';
import { IUser } from '../../../../shared/models/be/user';
import { RequestsService } from '../../../../core/services/utils/requests.service';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatDialog } from '@angular/material/dialog';
import { IonContent } from '@ionic/angular/standalone';
import { environment } from '../../../../../environments/environment';
import { MonitoringOutletComponent } from '../../../../shared/components/monitoring-outlet/monitoring-outlet.component';
import { GuideService } from '../../../../core/services/guide.service';
import { GuideTourComponent } from '../../../../shared/components/guide-tour/guide-tour.component';

@Component({
  selector: 'app-root-mobile',
  templateUrl: './root.mobile.page.html',
  styleUrls: ['./root.mobile.page.scss'],
  imports: [TutorialScrollLockDirective,
    GuideTourComponent,
    MonitoringOutletComponent,
    CommonModule,
    FormsModule,
    RouterModule,
    MatSidenavModule,
    MatGridListModule,
    MatSidenavModule,
    MatListModule,
    MatExpansionModule,
    MatIconModule,
    TranslateModule,
    MatToolbarModule,
    IonContent
  ]
})
export class RootMobilePage implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('drawer') drawer!: MatSidenav;
  @ViewChildren('menuItemElement', { read: ElementRef }) menuItemElements!: QueryList<ElementRef<HTMLElement>>;
  showSubmenu: boolean = false;
  isShowing = false;
  showSubSubMenu: boolean = false;
  public loggedUser?: IUser;
  public buildingName: string = '';
  public menuItems: IMenuItem[] = [];
  public environment = environment;
  public isDrawerOpen: boolean = false;
  public tutorialActive: boolean = false;
  public tutorialStepIndex: number = -1;

  private guideSubscription?: Subscription;

  constructor(
    private authService: AuthService,
    public dialog: MatDialog,
    private requestsService: RequestsService,
    public guide: GuideService
  ) {
    this.loggedUser = requestsService.getCurrentUser() || undefined;
    requestsService.currentUser$.pipe(
      tap((currentUser: IUser | null) => {
        this.loggedUser = currentUser || undefined;
        this.buildingName = currentUser?.building?.name || '';
      })
    ).subscribe();
  }

  ngOnInit() {
    this.loggedUser = this.requestsService.getCurrentUser() || undefined;
    this.menuItems = [
      {
        id: 'mentorage',
        label: 'MENTORAGE',
        icon: 'mentorage',
        routerLink: 'monitoring',
        isSvgIcon: true
      },
      {
        id: 'alertsHistory',
        label: 'ALERTS_HISTORY',
        icon: 'menu_book',
        routerLink: 'alertsHistory'
      },
      {
        id: 'settings',
        label: 'SETTINGS',
        icon: 'settings',
        routerLink: 'settings'
      },
      {
        id: 'logout',
        label: 'LOGOUT',
        icon: 'logout',
        routerLink: ''
      }
    ];
    this.menuItems.splice(this.menuItems.length - 1, 0, {
      id: 'guide', label: 'GUIDE', icon: 'help_outline', routerLink: 'guide'
    });

    if (['superuser', 'localadmin'].includes(this.loggedUser?.role || '')) {
      this.menuItems.splice(2, 0, {
        id: 'analytics',
        label: 'ANALYTICS',
        icon: 'analytics',
        routerLink: 'analytics'
      });
    }
    this.guide.setAnalyticsAvailable(this.menuItems.some(item => item.id === 'analytics'));
  }

  ngAfterViewInit(): void {
    this.guideSubscription = this.guide.request$.subscribe(module => {
      if (module === 'menu' || module === 'openMentorage' || module === 'openHistory' || module === 'openSettings' || module === 'openAnalytics') this.startTutorial();
      else if (this.tutorialActive) {
        this.tutorialActive = false;
        this.drawer.close();
      }
    });
  }

  toggleDrawer(): void {
    if (this.tutorialActive && this.tutorialStepIndex === -1) {
      this.openMenuForTutorial();
      return;
    }

    this.drawer.toggle();
  }

  openMenuForTutorial(): void {
    this.drawer.open().then(() => {
      this.tutorialStepIndex = 0;
      this.scrollCurrentTutorialItemIntoView();
    });
  }

  onMenuItemClick(event: Event, item: IMenuItem): void {
    if (this.tutorialActive) {
      event.preventDefault();
      if (this.isGuideNavigationStep && item.id === this.guide.navigationMenuItemId) {
        void this.drawer.close().then(() => this.guide.enterRequestedPage());
      }
      return;
    }

    if (item.id === 'logout') {
      this.logout();
    }
    this.drawer.close();
  }

  nextTutorialStep(): void {
    if (this.isGuideNavigationStep) return;
    if (this.tutorialStepIndex >= this.menuItems.length - 1) {
      this.finishTutorial();
      return;
    }

    this.tutorialStepIndex += 1;
    this.scrollCurrentTutorialItemIntoView();
  }

  previousTutorialStep(): void {
    if (this.tutorialStepIndex <= 0) {
      this.tutorialStepIndex = -1;
      this.drawer.close();
      return;
    }

    this.tutorialStepIndex -= 1;
    this.scrollCurrentTutorialItemIntoView();
  }

  skipTutorial(): void {
    void this.guide.cancel();
  }

  get currentTutorialItem(): IMenuItem | undefined {
    if (this.isGuideNavigationStep) return this.menuItems.find(item => item.id === this.guide.navigationMenuItemId);
    return this.menuItems[this.tutorialStepIndex];
  }

  get isGuideNavigationStep(): boolean { return this.guide.activeModule === 'openMentorage' || this.guide.activeModule === 'openHistory' || this.guide.activeModule === 'openSettings' || this.guide.activeModule === 'openAnalytics'; }

  getTutorialTranslationKey(field: 'TITLE' | 'DESCRIPTION'): string {
    const itemId = this.currentTutorialItem?.id.toUpperCase() || '';
    return `MOBILE_TUTORIAL.ITEMS.${itemId}.${field}`;
  }

  ngOnDestroy(): void {
    this.guideSubscription?.unsubscribe();
  }

  private startTutorial(): void {
    if ((this.guide.activeModule !== 'menu' && !this.isGuideNavigationStep) || !this.menuItems.length) return;
    this.drawer.close();
    this.tutorialStepIndex = -1;
    this.tutorialActive = true;
  }

  private finishTutorial(): void {
    this.tutorialActive = false;
    this.tutorialStepIndex = -1;
    void this.drawer.close().then(() => this.guide.completeModule());
  }

  private scrollCurrentTutorialItemIntoView(): void {
    setTimeout(() => {
      const target = this.menuItemElements.get(this.tutorialStepIndex)?.nativeElement;
      if (target) {
        target.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    });
  }

  logout() {
    this.authService.logout$().subscribe();
  }

  reloadPage() {
    window.location.reload();
  }
}

export interface IMenuItem {
  /**
   * Identificativo della voce di menu
   */
  id: string;
  /**
   * Label della voce di menu
   */
  label: string;
  /**
   * Icona, N.B. Deve essere di Angular Material l'icona
   */
  icon: string;
  /**
   * Route della pagina in cui si vuole navigare, opzionale nel caso siano presenti figli
   */
  routerLink?: string;
  /**
   * Se true richiede la presenza di una organizzazione, qualora non presente comparirà un Popup
   */
  requireOrganization?: boolean;
  /**
   * Opzionale, se presenti figli si inseriscono qua, il DTO è lo stesso di IMenuItem (ma senza children, non si possono annidare voci di menu più volte)
   */
  children?: Omit<IMenuItem, 'children'>[];
  /**
   * Opzionale, se true non viene mostrata la voce di menu
   */
  hide?: boolean;
  isSvgIcon?: boolean;
}
